var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import mergeRefs from 'merge-refs';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { InputPrivate, SIZE } from '@snack-uikit/input-private';
import { Slider } from '@snack-uikit/slider';
import { extractSupportProps, useEventHandler } from '@snack-uikit/utils';
import { CONTAINER_VARIANT, VALIDATION_STATE } from '../../constants';
import { FieldContainerPrivate } from '../../helperComponents';
import { usePostfix, usePrefix, useValueControl } from '../../hooks';
import { FieldDecorator } from '../FieldDecorator';
import { generateAllowedValues, getClosestMark, getTextFieldValue, isMarkObject } from './helpers';
import styles from './styles.module.css';
const getDefaultValue = (range, min, max, value) => {
    if (range) {
        if (value) {
            return value;
        }
        return [min, max];
    }
    return value !== null && value !== void 0 ? value : min;
};
export const FieldSlider = forwardRef((_a, ref) => {
    var { id, name, min, max, step, marks, showScaleBar = true, value: valueProp, range = false, disabled = false, readonly = false, onChange: onChangeProp, onFocus, onBlur, className, label, labelTooltip, labelTooltipPlacement, required, caption, hint, showHintIcon, size = SIZE.S, textInputFormatter, unbindInputFromMarks, postfixIcon, prefix, postfix } = _a, rest = __rest(_a, ["id", "name", "min", "max", "step", "marks", "showScaleBar", "value", "range", "disabled", "readonly", "onChange", "onFocus", "onBlur", "className", "label", "labelTooltip", "labelTooltipPlacement", "required", "caption", "hint", "showHintIcon", "size", "textInputFormatter", "unbindInputFromMarks", "postfixIcon", "prefix", "postfix"]);
    const [value = getDefaultValue(range, min, max, valueProp), onChange] = useValueControl({
        value: valueProp,
        defaultValue: getDefaultValue(range, min, max, valueProp),
        onChange: onChangeProp,
    });
    const [textFieldInputValue, setTextFieldInputValue] = useState(getTextFieldValue(value, textInputFormatter));
    const localRef = useRef(null);
    const prefixSettings = usePrefix({ prefix, disabled });
    const postfixSettings = usePostfix({ postfix, disabled });
    const getMarkValue = useCallback((key) => {
        const mark = marks[key];
        if (isMarkObject(mark)) {
            return mark.label;
        }
        return mark;
    }, [marks]);
    const hasMarksEqualToValues = useMemo(() => Object.keys(marks).every(key => key === getMarkValue(key)), [getMarkValue, marks]);
    const onTextFieldChange = (textFieldValue) => {
        const numValue = parseInt(textFieldValue);
        if (textFieldValue && Number.isNaN(numValue)) {
            return;
        }
        setTextFieldInputValue(textFieldValue);
    };
    const handleNonEqualMarksSliderChange = (textFieldNumValue) => {
        const handleChange = (key) => {
            setTextFieldInputValue(String(getMarkValue(key)));
            onChange(Number(key));
        };
        const allowedValues = Object.keys(marks).map(key => ({
            key,
            value: parseInt(String(getMarkValue(key))),
        }));
        const suitableEntry = allowedValues.find(entry => entry.value === textFieldNumValue);
        if (suitableEntry) {
            handleChange(suitableEntry.key);
            return;
        }
        const actualMin = parseInt(String(getMarkValue(min)));
        const actualMax = parseInt(String(getMarkValue(max)));
        if (textFieldNumValue < actualMin) {
            handleChange(min);
            return;
        }
        if (textFieldNumValue > actualMax) {
            handleChange(max);
            return;
        }
        const { mark } = getClosestMark(textFieldNumValue, allowedValues, mark => mark.value);
        handleChange(mark.key);
    };
    const handleEqualMarksSliderChange = (textFieldNumValue) => {
        const handleChange = (value) => {
            setTextFieldInputValue(String(value));
            onChange(value);
        };
        if (textFieldNumValue <= min) {
            handleChange(min);
            return;
        }
        if (textFieldNumValue >= max) {
            handleChange(max);
            return;
        }
        if (step === null) {
            const allowedValues = Object.keys(marks).map(Number);
            if (allowedValues.includes(textFieldNumValue)) {
                setTextFieldInputValue(String(textFieldNumValue));
                handleChange(textFieldNumValue);
                return;
            }
            const { mark } = getClosestMark(textFieldNumValue, allowedValues, mark => mark);
            handleChange(mark);
            return;
        }
        const allowedValues = generateAllowedValues(min, max, step);
        if (allowedValues.includes(textFieldNumValue)) {
            handleChange(textFieldNumValue);
            return;
        }
        const { mark } = getClosestMark(textFieldNumValue, allowedValues, mark => mark);
        handleChange(mark);
    };
    const handleTextValueChange = useEventHandler(() => {
        if (range) {
            return;
        }
        const parsedValue = parseInt(textFieldInputValue);
        const actualMinByMark = parseInt(String(getMarkValue(min)));
        const actualMin = Number.isNaN(actualMinByMark) ? min : actualMinByMark;
        const textFieldNumValue = textFieldInputValue ? parsedValue : actualMin;
        if (Number.isNaN(textFieldNumValue)) {
            return;
        }
        if (hasMarksEqualToValues || unbindInputFromMarks) {
            handleEqualMarksSliderChange(textFieldNumValue);
        }
        else {
            handleNonEqualMarksSliderChange(textFieldNumValue);
        }
    });
    const onTextFieldBlur = (e) => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        handleTextValueChange();
    };
    const handleTextFieldKeyChange = (e) => {
        if (e.key === 'Enter') {
            handleTextValueChange();
        }
    };
    useEffect(() => {
        setTextFieldInputValue(getTextFieldValue(value, textInputFormatter));
    }, [value, textInputFormatter]);
    useEffect(() => {
        handleTextValueChange();
    }, [marks, min, max, handleTextValueChange]);
    return (_jsxs(FieldDecorator, Object.assign({ className: className, label: label, labelTooltip: labelTooltip, labelTooltipPlacement: labelTooltipPlacement, labelFor: id, disabled: disabled, required: required, caption: caption, hint: hint, showHintIcon: showHintIcon, readonly: readonly, size: size }, extractSupportProps(rest), { children: [_jsx(FieldContainerPrivate, { className: styles.fieldContainer, size: size, validationState: VALIDATION_STATE.Default, disabled: disabled, readonly: readonly, variant: CONTAINER_VARIANT.SingleLine, inputRef: localRef, prefix: prefixSettings.show && prefixSettings.render({ key: prefixSettings.id }), postfix: _jsxs(_Fragment, { children: [postfixSettings.show && postfixSettings.render({ key: postfixSettings.id }), postfixIcon] }), children: _jsx(InputPrivate, { ref: mergeRefs(ref, localRef), "data-size": size, value: textFieldInputValue, onChange: range ? undefined : onTextFieldChange, onFocus: onFocus, onBlur: range ? onBlur : onTextFieldBlur, onKeyDown: handleTextFieldKeyChange, disabled: disabled, readonly: range ? true : readonly, type: 'text', id: id, name: name, "data-test-id": 'field-slider__input' }) }), _jsx("div", { className: styles.sliderWrapper, children: _jsx("div", { className: styles.slider, "data-size": size, children: _jsx(Slider, { range: range, min: min, max: max, step: step, value: value, onChange: onChange, marks: showScaleBar ? marks : undefined, disabled: readonly || disabled, "data-test-id": 'field-slider__slider' }) }) })] })));
});
